import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool, faChalkboardTeacher, faUsers, faUserGraduate } from '@fortawesome/free-solid-svg-icons';
import DashboardService from '../../services/dashboard.service';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

const CoordinatorDashboard = ({ user }) => {
  const { selectedSchool } = useSchool();
  const { selectedYear } = useAcademicYear();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    classes: 0,
    teachers: 0,
    students: 0,
    loadingStats: true
  });

  const getAddress = (school) => {
    const parts = [];
    if (school.city?.name) parts.push(school.city.name);
    if (school.street) parts.push(school.street);
    if (school.number) parts.push(school.number);
    if (school.neighborhood) parts.push(school.neighborhood);
    return parts.join(', ');
  };

  useEffect(() => {
    if (user?.school_users) {
      const coordSchools = user.school_users.filter(
        su => su.role === 'COORD' && su.active
      );
      setSchools(coordSchools);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (selectedSchool && selectedYear) {
      fetchStats(selectedSchool, selectedYear);
    }
  }, [selectedSchool, selectedYear]);

  const fetchStats = async (schoolId, year) => {
    try {
      setStats(prev => ({ ...prev, loadingStats: true }));

      const data = await DashboardService.getStats({
        school: schoolId,
        year: year
      });

      setStats({
        classes: data.stats?.classes || 0,
        teachers: data.stats?.teachers || 0,
        students: data.stats?.students || 0,
        loadingStats: false
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        classes: 0,
        teachers: 0,
        students: 0,
        loadingStats: false
      });
    }
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Dashboard do Coordenador</h4>
          <p className="mb-0">Bem-vindo(a), {user?.name}</p>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <Row className="mb-4">
        <Col xs={12} sm={6} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-primary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faChalkboardTeacher} size="lg" />
                  </div>
                </Col>
                <Col xs={12} xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Turmas</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.classes}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-secondary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faUsers} size="lg" />
                  </div>
                </Col>
                <Col xs={12} xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Professores</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.teachers}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row className="d-block d-xl-flex align-items-center">
                <Col xl={5} className="text-xl-center d-flex align-items-center justify-content-xl-center mb-3 mb-xl-0">
                  <div className="icon icon-shape icon-md icon-shape-tertiary rounded me-4 me-sm-0">
                    <FontAwesomeIcon icon={faUserGraduate} size="lg" />
                  </div>
                </Col>
                <Col xs={12} xl={7} className="px-xl-0">
                  <div className="d-none d-sm-block">
                    <h5 className="text-muted mb-1">Alunos</h5>
                    {stats.loadingStats ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <h3 className="mb-0">{stats.students}</h3>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="mb-4">
        <h5 className="mb-3">Minhas Escolas</h5>
      </div>

      {schools.length > 0 ? (
        schools.map((schoolUser, index) => (
          <Card border="light" className="shadow-sm mb-4" key={index}>
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <div className="d-flex align-items-start mb-3">
                    {schoolUser.school?.logo ? (
                      <img
                        src={schoolUser.school.logo}
                        alt={`Logo ${schoolUser.school.name}`}
                        className="rounded me-3"
                        style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                      />
                    ) : (
                      <div className="icon icon-shape icon-lg icon-shape-primary rounded me-3">
                        <FontAwesomeIcon icon={faSchool} size="lg" />
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <h4 className="mb-1">{schoolUser.school?.name || 'Carregando...'}</h4>
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="info">
                          {schoolUser.role_display || 'Coordenador'}
                        </Badge>
                        <Badge bg="success">
                          Ativo
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <Row className="mt-4">
                    <Col sm={6} className="mb-3">
                      <div className="d-flex flex-column">
                        <span className="text-muted small mb-1">Município</span>
                        <span className="fw-bold">{schoolUser.school?.city?.name || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6} className="mb-3">
                      <div className="d-flex flex-column">
                        <span className="text-muted small mb-1">CNPJ</span>
                        <span className="fw-bold">{schoolUser.school?.cnpj || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6} className="mb-3">
                      <div className="d-flex flex-column">
                        <span className="text-muted small mb-1">Telefone</span>
                        <span className="fw-bold">{schoolUser.school?.phone || '-'}</span>
                      </div>
                    </Col>
                    <Col sm={6} className="mb-3">
                      <div className="d-flex flex-column">
                        <span className="text-muted small mb-1">Endereço</span>
                        <span className="fw-bold">{getAddress(schoolUser.school)}</span>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))
      ) : (
        <Card border="light" className="shadow-sm">
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon icon={faSchool} size="3x" className="text-muted mb-3" />
            <h5 className="text-muted">Nenhuma escola encontrada</h5>
            <p className="text-muted">Você não está vinculado como coordenador em nenhuma escola.</p>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default CoordinatorDashboard;
